import {
  AddMyBookmarkDocument,
  type BookmarkEntityType,
  RemoveMyBookmarkDocument,
} from '@mdvip/shared/graphql/generated/graphql';
import { useToast } from '@mdvip/shared/hooks/use-toast';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { gqlRequest } from '@mdvip/shared/lib/graphql-client';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * How one content type's query cache reflects a bookmark change. The hook owns the request, the
 * toast and the telemetry; everything that knows a query key lives behind this interface, which is
 * what lets Nutrition Articles reuse the hook without it learning about recipes.
 */
export interface BookmarkCacheUpdater<TSnapshot> {
  /** Optimistic patch. Returns whatever `rollback` needs to put things back. */
  apply(entityId: string, next: boolean): TSnapshot;
  rollback(snapshot: TSnapshot): void;
  /** Called on settle, success and error alike, so the control lands on server truth. */
  invalidate(entityId: string): Promise<void>;
}

export interface ToggleBookmarkArgs {
  entityId: string;
  title: string;
  next: boolean;
}

interface ToggleBookmarkVariables extends ToggleBookmarkArgs {
  /** Undo re-toggles silently — the member already saw a toast for the change they're reversing. */
  silent: boolean;
}

/**
 * Toggling a bookmark on any content type: optimistic flip, confirmation toast with Undo once the
 * server agrees, rollback plus a destructive toast when it doesn't.
 *
 * Built through the mutation cache rather than `useMutation` because `scope` is read off the
 * mutation's own options: one `useMutation` per page would give every card the same scope and
 * serialize taps on unrelated recipes against each other. Per-entity scoping instead lets rapid
 * taps on one item queue in order — `onMutate` still runs immediately on each tap, only the
 * request waits — so the item ends on the member's last choice.
 */
export function useToggleBookmark<TSnapshot>(entityType: BookmarkEntityType, cache: BookmarkCacheUpdater<TSnapshot>) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('shared');
  const tError = useErrorTranslation();

  const toggle = useCallback(
    (args: ToggleBookmarkArgs) => {
      // Named so Undo can re-enter it without `toggle` depending on itself.
      const run = (variables: ToggleBookmarkVariables) => {
        const mutation = queryClient
          .getMutationCache()
          .build<void, unknown, ToggleBookmarkVariables, TSnapshot>(queryClient, {
            scope: { id: `bookmark:${entityType}:${variables.entityId}` },
            mutationFn: async ({ entityId, next }) => {
              const input = { entityType, entityId, kind: 'BOOKMARK' as const };
              if (next) await gqlRequest(AddMyBookmarkDocument, { input });
              else await gqlRequest(RemoveMyBookmarkDocument, { input });
            },
            onMutate: ({ entityId, next }) => cache.apply(entityId, next),
            onSuccess: (_data, { entityId, title, next, silent }) => {
              if (silent) return;
              toast({
                variant: 'success',
                title: next ? t('bookmarks.toast.saved') : t('bookmarks.toast.removed'),
                description: title,
                action: {
                  label: t('bookmarks.toast.undo'),
                  onClick: () => run({ entityId, title, next: !next, silent: true }),
                },
              });
            },
            onError: (error, _variables, snapshot) => {
              if (snapshot !== undefined) cache.rollback(snapshot);
              const code = extractGqlErrorCode(error);
              // Never the raw client error: it embeds the owner and entity ids.
              trackException(new Error(`toggleBookmark failed: ${code}`), { source: 'toggleBookmark', entityType });
              toast({ title: tError('title'), description: tError(code), variant: 'destructive' });
            },
            onSettled: (_data, _error, { entityId }) => cache.invalidate(entityId),
          });

        // `execute` rejects after onError has run; that handler is the whole response to a failure.
        void mutation.execute(variables).catch(() => {});
      };

      run({ ...args, silent: false });
    },
    [queryClient, entityType, cache, toast, t, tError],
  );

  return { toggle };
}
